export const clearSelectionId = "__cp_select_clear_selection__";
export const emptyStateItemId = "__cp_select_empty_state_item__";

export type SelectDataItem = {
  id: string | number;
  name?: string;
  subName?: string;
  subHeader?: boolean;
  [key: string]: any;
};

export type SelectGroupData = {
  id: string | number;
  name: string;
  data: SelectDataItem[];
  [key: string]: any;
};

export type TransformDataFn = (item: any) => SelectDataItem;
export type SearchFilterFn = (item: SelectDataItem) => boolean;

export const defaultSearchFilter = (
  searchFilter: boolean | SearchFilterFn | undefined,
  searchValue: string | undefined,
): SearchFilterFn | undefined => {
  if (!searchFilter) return undefined;
  if (typeof searchFilter !== "function") {
    return searchValue ? filterAlpha(searchValue) : undefined;
  }
  return searchFilter;
};

/**
 * Filter Alpha
 * @param {string} search
 * @param {string|string[]} key
 * @returns {function}
 */
export const filterAlpha = (
  search: string,
  key: string | string[] = ["name", "subName"],
): SearchFilterFn => {
  return (item: SelectDataItem): boolean => {
    const keys = Array.isArray(key) ? key : [key];
    const regex = new RegExp(
      search.replace(/[#-.]|[[-^]|[?|{}]/g, "\\$&"),
      "i",
    );
    if (search) {
      return !!keys.find((k) => {
        if (!item.hasOwnProperty(k)) return false; // eslint-disable-line no-prototype-builtins
        const value = item[k];
        if (typeof value !== "string" && typeof value !== "number") {
          console.warn(
            `CpSelect.filterAlpha: search property "${k}" of type "${typeof value}" is invalid. Search properties require either "string" or "number" types.`,
          );
          return false;
        }
        return !!value.toString().match(regex);
      });
    }
    return true;
  };
};

/**
 * Node List To Array
 * @param {NodeList} nodeList
 * @returns {Node[]}
 */
export const nodeListToArray = (nodeList: NodeListOf<Element>): Element[] => {
  let i = nodeList.length;
  const arr = new Array<Element>(i);
  while (i--) {
    arr[i] = nodeList[i];
  }
  return arr;
};

function validateData(data: any[], hasDataTransform: boolean): boolean {
  const err: string[] = [];
  for (let i = 0; i < data.length; i++) {
    if (typeof data[i] !== "object") {
      err.push(
        `"data" prop must be an array of objects. Current type: ${typeof data[
          i
        ]}`,
      );
      break;
    }
    const { id, name, subName } = data[i];
    // eslint-disable-next-line no-prototype-builtins
    if (!data[i].hasOwnProperty("id")) {
      err.push(`data property "id" is required`);
    }
    if (typeof id !== "string" && typeof id !== "number") {
      err.push(`data property "id" must be of type "string" or "number"`);
    }
    // eslint-disable-next-line no-prototype-builtins
    if (!data[i].hasOwnProperty("name")) {
      err.push(`data property "name" is required`);
    }
    if (typeof name !== "string") {
      err.push(`data property "name" must be of type "string"`);
    }
    if (subName && typeof subName !== "string") {
      err.push(`data property "subName" must be of type "string"`);
    }
    if (err.length) {
      if (hasDataTransform) {
        err.forEach((e) => console.error(`[CpSelect] ${e}`));
      } else {
        console.error(
          `[CpSelect] "data" prop is using invalid properties in the collection. Try using the "transformData" to transform it.`,
        );
      }
      console.error(
        `[CpSelect] "data" valid prop structure: Array<{ id: string|number, name: string, subName?: string }>`,
      );
      break;
    }
  }
  return !err.length;
}

/**
 *  * Build Data
 * Apply filters and transforms to data
 * @param {object[]} data
 * @param {function=} transformData
 * @param {function=} searchFilter
 * @returns {{ data: array, dataMap: object }}
 */
export function buildData(
  data: any[],
  transformData: TransformDataFn | undefined,
  searchFilter: SearchFilterFn | undefined,
  searchValue?: string,
): { data: SelectDataItem[]; dataMap: Record<string | number, any> } {
  const dataMap: Record<string | number, any> = {};
  const dataTransformed = transformData
    ? data.map((item) => {
        const newItem = transformData(item);
        dataMap[newItem.id] = item;
        return newItem;
      })
    : data;

  if (
    !validateData(!!transformData ? dataTransformed : data, !!transformData)
  ) {
    return { data: [], dataMap: {} };
  }
  return {
    data: searchFilter
      ? dataTransformed.filter(
          (d) => searchFilter(d) && (searchValue ? !d.subHeader : true),
        )
      : dataTransformed,
    dataMap,
  };
}

/**
 *  * Build Group Data
 * Apply filters and transforms to group data
 * @param {object[]} data
 * @param {function=} transformData
 * @param {function=} searchFilter
 * @param {string=} searchValue
 * @returns {{ data: array, dataMap: object }}
 */
export function buildGroupData(
  data: SelectGroupData[],
  transformData: TransformDataFn | undefined,
  searchFilter: SearchFilterFn | undefined,
  searchValue?: string,
): { data: SelectGroupData[]; dataMap: Record<string | number, any> } {
  const dataMap: Record<string | number, any> = {};
  // transform data
  const dataTransformed = data.map((group) => {
    return {
      ...group,
      data: transformData
        ? group?.data.map((item) => {
            const newItem = transformData(item);
            dataMap[newItem.id] = item;
            return newItem;
          })
        : group?.data,
    };
  });
  return {
    // apply search filter
    data: searchFilter
      ? dataTransformed
          .map((group) => ({
            ...group,
            data: group.data.filter(
              (d) => searchFilter(d) && (searchValue ? !d.subHeader : true),
            ),
          }))
          .filter((group) => group.data.length)
      : dataTransformed,
    dataMap,
  };
}

export const getItemElements = (
  ref: React.RefObject<HTMLElement>,
): Element[] | undefined => {
  if (!ref.current) return;
  return nodeListToArray(
    ref.current.querySelectorAll(
      `.cp-select-component__content-items > [data-id]`,
    ),
  );
};

export const scrollToElement = (el: Element | null): void => {
  if (el) el.scrollIntoView({ block: "nearest" });
};
